/* eslint-disable react/display-name */

import html2canvas from "html2canvas";
import moment from "moment";
import React, { FC, useMemo, useRef, useState } from "react";
import { Checkbox } from "@material-ui/core";
import { GridColDef } from "@material-ui/data-grid";
import { jsPDF } from "jspdf";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import BaseDataGrid from "../../../components/modules/BaseDataGrid";
import InvoicesDashboardToolbar from "./InvoicesDashboardToolbar";
import Select from "../../../components/elements/Select";
import classes from "./InvoicesDashboard.module.scss";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";
import { SelectOptionType } from "../../../components/elements/Select/Select";
import { transformRowValue } from "../../../helpers/campainRowFormatHelpers";

type InvoicesDashboardProps = {
  invoices: any[];
};

const InvoicesDashboard: FC<InvoicesDashboardProps> = ({ invoices }) => {
  const exportRef = useRef<HTMLDivElement>(null);
  const advertisers = useSelector((state: ReduxApplicationState) => state.agency.advertisers);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const advertiserId = searchParams.get("advertiser");

  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);

  const options: SelectOptionType[] = advertisers.map(({ name, brandgenAdvertiserId }) => ({
    label: name,
    value: brandgenAdvertiserId,
  }));

  const [selectedAdvertiser, setSelectedAdvertiser] = useState(advertiserId ?? "");

  const handleChangeAdvertiserDetailToView = (e: any) => {
    const brandgenAdvertiserId = (e.target as HTMLSelectElement).value;
    setSelectedAdvertiser(brandgenAdvertiserId);
  };

  const filteredInvoices = useMemo(
    () =>
      selectedAdvertiser ? invoices.filter((invoice) => invoice.brandgenAdvertiserId === selectedAdvertiser) : invoices,
    [invoices, selectedAdvertiser],
  );

  const rows = filteredInvoices.map(
    ({ sentAt, recipientEmail, source, subject, body, brandgenAdvertiserId, advertiser, chargeAmount }, index) => ({
      id: `${sentAt}-${index}`,
      sentAt: moment(sentAt).format("MMM Do YYYY, h:mm:ss a"),
      chargeAmount: transformRowValue(chargeAmount, false, true, "CURRENCY"),
      recipientEmail,
      source,
      subject,
      body,
      brandgenAdvertiserId,
      advertiserName: advertiser?.name ?? "N/A",
    }),
  );

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedRows(rows);
      setIsAllSelected(true);
    } else {
      setSelectedRows([]);
      setIsAllSelected(false);
    }
  };

  const handleCheckboxChange = (row: any) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedRows((prev) => [...prev, row]);
    } else {
      setSelectedRows((prev) => prev.filter((selected) => selected.id !== row.id));
    }
  };

  const columns: GridColDef[] = [
    {
      field: "checkbox",
      headerName: "Select All",
      disableColumnMenu: true,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderHeader: () => (
        <div className={classes.headerCheckbox}>
          <Checkbox
            checked={isAllSelected}
            onChange={handleSelectAllClick}
            indeterminate={selectedRows.length > 0 && selectedRows.length < rows.length}
          />
        </div>
      ),
      width: 60,
      renderCell: (params: any) => (
        <Checkbox
          color="primary"
          checked={selectedRows.some((row) => row.id === params.row.id)}
          onChange={handleCheckboxChange(params.row)}
        />
      ),
    },
    { field: "sentAt", headerName: "Date", width: 220 },
    { field: "chargeAmount", headerName: "Charge Amount", width: 180 },
    { field: "recipientEmail", headerName: "Recipient Email", width: 250 },
    { field: "source", headerName: "Source", width: 250 },
    { field: "subject", headerName: "Subject", width: 250 },
    { field: "advertiserName", headerName: "Advertiser Name", width: 250 },
  ];

  const handleExportSelectedToPdf = async () => {
    setIsLoading(true);

    setTimeout(async () => {
      try {
        for (const row of selectedRows) {
          const tempDiv = document.createElement("div");
          tempDiv.style.position = "absolute";
          tempDiv.style.left = "-9999px";
          tempDiv.style.visibility = "visible";
          tempDiv.style.width = "800px";
          tempDiv.innerHTML = row.body;
          document.body.append(tempDiv);

          /* eslint-disable no-await-in-loop */
          const canvas = await html2canvas(tempDiv, {
            scale: 2,
            useCORS: true,
            onclone: (document) => {
              return new Promise<void>((resolve) => {
                const images = document.querySelectorAll("img");
                let loadedCount = 0;

                if (images.length === 0) {
                  resolve();
                  return;
                }

                const checkDone = () => {
                  loadedCount++;
                  if (loadedCount === images.length) {
                    resolve();
                  }
                };

                images.forEach((img) => {
                  if (img.complete) {
                    checkDone();
                  } else {
                    img.addEventListener("load", checkDone);
                    img.addEventListener("error", checkDone);
                  }
                });
              });
            },
          });

          const imageData = canvas.toDataURL("image/png");

          // eslint-disable-next-line new-cap
          const pdf = new jsPDF("p", "mm", "a4");
          const pageWidth = pdf.internal.pageSize.getWidth();
          const imgHeight = (canvas.height * pageWidth) / canvas.width;

          pdf.addImage(imageData, "PNG", 0, 0, pageWidth, imgHeight);
          const formattedId = row.id.split(".")[0].replace(/:/g, "-");

          pdf.save(`Invoice-${formattedId}.pdf`);

          tempDiv.remove();
        }
      } catch (error) {
        console.error("Error exporting selected rows to PDF:", error);
      } finally {
        setIsLoading(false);
        setSelectedRows([]);
        setIsAllSelected(false);
      }
    }, 0);
  };

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <div className={classes.dashboardContainer}>
          <Select
            className={classes.select}
            label="Select"
            options={options}
            value={selectedAdvertiser}
            onChange={handleChangeAdvertiserDetailToView}
          />
          <div className={classes.dashboard} ref={exportRef}>
            <BaseDataGrid
              columns={columns}
              rows={rows}
              components={{
                Toolbar: () => (
                  <InvoicesDashboardToolbar
                    onExport={handleExportSelectedToPdf}
                    isExportDisabled={selectedRows.length === 0}
                    isLoading={isLoading}
                  />
                ),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicesDashboard;
